import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { customersApiService } from '../../services';

function Customers() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const getAllCustomers = async () => {
    try {
      const response = await customersApiService.getAllCustomers();
      setData(response);
    } catch {
      console.log('Error fetching customers:', error);
      setError('Error fetching customers');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this customer?'))
      return;

    try {
      // Note: Delete functionality needs to be verified/implemented in external API
      await customersApiService.deleteCustomer?.(id);
      setData(prevData => prevData.filter(customer => customer.id !== id));
    } catch (err) {
      console.error('Error deleting customer:', err);
      alert('Failed to delete customer. Delete may not be supported by external API.');
    }
  };

  const exportToCSV = () => {
    if (data.length === 0) return;

    const headers = [
      'Customer Number',
      'Customer Name',
      'Platform',
      'Edgemarc',
      'Edgemarc MAC Address',
      'Edgemarc WAN 1',
      'Edgemarc WAN 2',
      'Edgemarc ETH',
      'Company Address',
      'Bicom Server',
      'Enterprise',
      'ISP Circuit ID',
    ];
    const rows = data.map(customer => [
      customer.customer_number,
      customer.customer_name,
      customer.platform,
      customer.edgemarc,
      customer.em_mac_address,
      customer.wan1,
      customer.wan2,
      customer.eth,
      customer.company_address,
      customer.server,
      customer.enterprise,
      customer.isp_circuit_id,
    ]);

    const csvContent = [
      headers.join(','), // encabezado
      ...rows.map(row => row.join(',')), // datos
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'customers.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    if (data.length === 0) return;

    // datos como objetos clave-valor
    const excelData = data.map(customer => ({
      'Customer Number': customer.customer_number,
      'Customer Name': customer.customer_name,
      Platform: customer.platform,
      Edgemarc: customer.edgemarc,
      'Edgemarc IP Address': customer.em_mac_address,
      'Edgemarc WAN 1': customer.wan1,
      'Edgemarc WAN 2': customer.wan2,
      'Edgemarc ETH': customer.eth,
      'Company Address': customer.company_address,
      'Bicom Server': customer.server,
      Enterprise: customer.enterprise,
      'ISP Circuit ID': customer.isp_circuit_id,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

    XLSX.writeFile(workbook, 'customers.xlsx');
  };

  useEffect(() => {
    getAllCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 flex flex-col items-center">
      {/* Botón para agregar clientes */}
      <div className="w-3/4 flex justify-end mb-2 gap-4">
        <button
          onClick={exportToExcel}
          className="px-4 py-2 flex items-center gap-2 bg-blue-600 text-white font-semibold border-b-4 rounded hover:bg-blue-500 border-blue-800 hover:border-blue-700"
        >
          Export Excel
        </button>

        <button
          onClick={exportToCSV}
          className="px-4 py-2 flex items-center gap-2 bg-red-500 text-white font-semibold border-b-4 rounded hover:bg-red-400 border-red-700 hover:border-red-600"
        >
          Export CSV
        </button>

        <Link
          to={'/addcustomers'}
          className="px-4 py-2 flex items-center gap-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          Add Customers
        </Link>
      </div>

      {/* Contenedor de la tabla con bordes redondeados */}
      <div className="w-3/4 overflow-hidden rounded-lg border border-black">
        <table className="w-full border-collapse">
          <thead className="bg-red-600 text-white">
            <tr>
              <th className="border border-black px-4 py-2 text-left">
                Customer Number
              </th>
              <th className="border border-black px-4 py-2 text-left">
                Customer Name
              </th>
              <th className="border border-black px-4 py-2 text-left">
                Platform
              </th>
              <th className="border border-black px-4 py-2 text-left">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="font-semibold">
            {data.length > 0 ? (
              data.map(customer => (
                <tr key={customer.id} className="hover:bg-red-100">
                  <td className="border border-black px-4 py-2">
                    {customer.customer_number}
                  </td>
                  <td className="border border-black px-4 py-2">
                    {customer.customer_name}
                  </td>
                  <td className="border border-black px-4 py-2">
                    {customer.platform}
                  </td>
                  <td className="border border-black py-2 text-center ">
                    <Link
                      to={`/addcustomers/${customer.id}`}
                      className="px-2 py-1 bg-blue-600 text-white font-semibold border-b-4 rounded hover:bg-blue-500 border-blue-800 hover:border-blue-700"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(customer.id)}
                      className="px-2 py-1 ml-2 bg-red-600 text-white font-semibold border-b-4 rounded hover:bg-red-700 border-red-800 hover:border-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="border border-gray-300 px-4 py-2 text-center text-gray-500"
                >
                  No hay datos disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Customers;
